import React from 'react';
import { FolderArchive } from 'lucide-react';

export type PePapersStorageHintData = {
    duty: string;
    location: string;
    rule: string;
};

/** Ein Quelle: Kachel + Modal — Keys = echte Modul-IDs in PePapersPage */
export const PE_PAPERS_STORAGE_HINTS: Record<string, PePapersStorageHintData> = {
    bgw: {
        duty: 'Kein Aushang erforderlich',
        location: 'Zentraler Arbeitsschutzordner',
        rule: 'Muss bei Prüfungen durch BGW und Gewerbeaufsicht sofort griffbereit vorliegen. Mitarbeitern ist jederzeit Einsicht zu gewähren.',
    },
    'bgw-uvv-unterweisung': {
        duty: 'Nachweis-Dokumentation (Unterschriftenliste)',
        location: 'Zentraler Arbeitsschutzordner / Nachweisordner Unterweisungen',
        rule: 'Unterschriebene Liste mindestens 5 Jahre aufbewahren (wichtiger Nachweis bei Arbeitsunfällen).',
    },
    dsgvo: {
        duty: 'Streng vertraulich (Nicht aushängen!)',
        location: 'Datenschutzordner der Geschäftsleitung',
        rule: 'Dient ausschließlich der behördlichen Vorlage bei Kontrollen nach Art. 30 DSGVO.',
    },
    gobd: {
        duty: 'Kassen- & Steuerprüfung (Kein Aushang)',
        location: 'Kassenordner am Arbeitsplatz / Steuerunterlagen',
        rule: 'Muss bei unangekündigter Kassen-Nachschau (§ 146b AO) oder Betriebsprüfung unmittelbar vorgelegt werden.',
    },
    'sachbezug-50': {
        duty: 'Vertragszusatz (Zweifach)',
        location: '1x Personal-/Lohnakte (für Steuerberater), 1x Arbeitnehmer/in',
        rule: 'Kein Aushang. Aufbewahrung für die Lohnsteuerprüfung.',
    },
    'mitfuehrpflicht-zeiterfassung': {
        duty: 'Personalakte & Aushang-Empfehlung',
        location: 'Unterschriebene Bestätigung in Lohn-/Personalakte (Zoll / FKS)',
        rule: 'Das Merkblatt zur Ausweismitführpflicht zusätzlich am Schwarzen Brett / im Personalraum aushängen.',
    },
};

export interface PePapersStorageHintProps {
    hint: PePapersStorageHintData;
    variant?: 'card' | 'modal';
    className?: string;
}

/**
 * UI-only Hinweis „Verarbeitung & Aufbewahrung“ — nie in PDF-Dokumentkörper.
 */
const PePapersStorageHint: React.FC<PePapersStorageHintProps> = ({
    hint,
    variant = 'card',
    className = '',
}) => {
    const pad = variant === 'modal' ? 'p-3' : 'p-2.5';

    return (
        <div
            className={`rounded-lg border border-slate-300 dark:border-slate-800 bg-stone-100/90 dark:bg-slate-900/90 ${pad} text-xs ${className}`}
            role="note"
            aria-label="Verarbeitung und Aufbewahrung"
        >
            <div className="flex items-start gap-2">
                <FolderArchive
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-pe-accent-text dark:text-[#E6C299]"
                    aria-hidden
                />
                <div className="min-w-0 space-y-1.5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]">
                        Verarbeitung &amp; Aufbewahrung
                    </p>
                    <p className="leading-snug">
                        <span className="font-bold text-pe-muted dark:text-slate-400">Status: </span>
                        <span className="font-semibold text-pe-text dark:text-slate-200">{hint.duty}</span>
                    </p>
                    <p className="leading-snug">
                        <span className="font-bold text-pe-muted dark:text-slate-400">Ort: </span>
                        <span className="text-pe-text dark:text-slate-300">{hint.location}</span>
                    </p>
                    <p className="leading-snug text-pe-muted dark:text-slate-400">{hint.rule}</p>
                </div>
            </div>
        </div>
    );
};

export default PePapersStorageHint;
